import React from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaLinkedin,
  FaGlobe,
  FaMapMarkerAlt,
  FaTrophy,
  FaGraduationCap,
  FaCode,
  FaBookOpen,
  FaBriefcase,
  FaLanguage,
  FaHeart,
} from "react-icons/fa";
import "./ProfessionalBulletTemplate.css";

const ProfessionalBulletTemplate = ({ resumeData }) => {
  if (!resumeData) return <div className="loading-placeholder">Loading...</div>;

  const {
    personal = {},
    experience = [],
    education = [],
    skills = { technical: [], soft: [], languages: [] },
    achievements = [],
    certifications = [],
    interests = [],
  } = resumeData;

  const allSkills = [...skills.technical, ...skills.soft];

  return (
    <div className="resume-template professional-bullet">
      {/* LEFT COLUMN (Dark) */}
      <div className="pb-left">
        {/* Key Achievements (bullet style) */}
        {achievements.length > 0 && (
          <div className="pb-section">
            <h3 className="pb-section-title">
              <FaTrophy /> KEY ACHIEVEMENTS
            </h3>
            <ul className="pb-achievements-list">
              {achievements.map((ach, idx) => (
                <li key={idx} className="pb-achievement-item">
                  <strong>{ach.split(".")[0] || ach.split("–")[0]}</strong>
                  <span>{ach}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Skills */}
        {allSkills.length > 0 && (
          <div className="pb-section">
            <h3 className="pb-section-title">
              <FaCode /> SKILLS
            </h3>
            <div className="pb-skills-list">
              {allSkills.map((skill, idx) => (
                <span key={idx} className="pb-skill-tag">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Training / Courses (Certifications) */}
        {certifications.length > 0 && (
          <div className="pb-section">
            <h3 className="pb-section-title">
              <FaBookOpen /> TRAINING / COURSES
            </h3>
            {certifications.map((cert, idx) => (
              <div key={cert.id || idx} className="pb-course-item">
                <div className="pb-course-title">{cert.name}</div>
                <div className="pb-course-issuer">{cert.issuer}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT COLUMN (White) */}
      <div className="pb-right">
        {/* Header: Name + Role + Contact */}
        <div className="pb-header">
          <h1 className="pb-name">{personal.fullName || "YOUR NAME"}</h1>
          <div className="pb-role">
            {personal.title || "The role you are applying for?"}
          </div>
          <div className="pb-contact">
            {personal.email && (
              <span>
                <FaEnvelope /> {personal.email}
              </span>
            )}
            {personal.phone && (
              <span>
                <FaPhoneAlt /> {personal.phone}
              </span>
            )}
            {personal.linkedin && (
              <span>
                <FaLinkedin /> LinkedIn
              </span>
            )}
            {personal.portfolio && (
              <span>
                <FaGlobe /> Portfolio
              </span>
            )}
            {personal.address && (
              <span>
                <FaMapMarkerAlt /> {personal.address}
              </span>
            )}
          </div>
        </div>

        {/* Summary */}
        {personal.summary && (
          <div className="pb-section-right">
            <h3 className="pb-section-title-right">SUMMARY</h3>
            <div className="pb-summary">{personal.summary}</div>
          </div>
        )}

        {/* Experience */}
        {experience.length > 0 && (
          <div className="pb-section-right">
            <h3 className="pb-section-title-right">
              <FaBriefcase /> EXPERIENCE
            </h3>
            {experience.map((exp, idx) => (
              <div key={exp.id || idx} className="pb-exp-item">
                <div className="pb-exp-title">{exp.title || "Title"}</div>
                <div className="pb-exp-company">
                  {exp.company || "Company Name"}
                </div>
                <div className="pb-exp-meta">
                  <span>{exp.startDate || "Date period"}</span>
                  <span>{exp.location || "Location"}</span>
                </div>
                <ul className="pb-exp-desc">
                  {exp.description?.split("\n").map((line, i) => (
                    <li key={i}>
                      {line ||
                        "Highlight your accomplishments, using numbers if possible."}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {education.length > 0 && (
          <div className="pb-section-right">
            <h3 className="pb-section-title-right">
              <FaGraduationCap /> EDUCATION
            </h3>
            {education.map((edu, idx) => (
              <div key={edu.id || idx} className="pb-edu-item">
                <div className="pb-edu-degree">
                  {edu.degree || "Degree and Field of Study"}
                </div>
                <div className="pb-edu-school">
                  {edu.institution || "School or University"}
                </div>
                <div className="pb-edu-meta">
                  <span>{edu.year || "Date period"}</span>
                  <span>{edu.location || "Location"}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Languages */}
        {skills.languages?.length > 0 && (
          <div className="pb-section-right">
            <h3 className="pb-section-title-right">
              <FaLanguage /> LANGUAGES
            </h3>
            <div className="pb-languages">
              {skills.languages.map((lang, idx) => (
                <span key={idx} className="pb-lang-item">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <div className="pb-section-right">
            <h3 className="pb-section-title-right">
              <FaHeart /> INTERESTS
            </h3>
            {interests.map((interest, idx) => (
              <div key={idx} className="pb-interest-item">
                <div className="pb-interest-title">{interest}</div>
                <div className="pb-interest-desc">
                  What does the company have which makes it attractive for you?
                  (e.g. learning opportunities)
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalBulletTemplate;
